import React, { useCallback, useEffect, useMemo } from "react";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useStyles } from "../../theme/appStyles";
import { usePermissions } from "../../Core/Permissions";
import {
  Avatar,
  Badge,
  Chip,
  Collapse,
  Grid,
  InputAdornment,
  ListItemButton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UserImg from "../../Images/user_login_photo.webp";
import { useState } from "react";
import { useThemeContext } from "./ThemeContext";
import Notification from "../../Components/Popup/notification";
import LogoMain from "../../Images/logo_main_black.webp";
import LogoFav from "../../Images/fav.png";
import useBranding from "../../Core/useBranding";

import { MdOutlineNotificationsNone } from "react-icons/md";
import { MdDarkMode } from "react-icons/md";
import { CiSearch } from "react-icons/ci";
import Profile from "../../Components/Popup/profile";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// ── Icons ──────────────────────────────────────────────────────────────────────
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import BlockIcon from "@mui/icons-material/Block";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import LoopIcon from "@mui/icons-material/Loop";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import LinkIcon from "@mui/icons-material/Link";
import AttachEmailOutlinedIcon from "@mui/icons-material/AttachEmailOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import AddCardOutlinedIcon from "@mui/icons-material/AddCardOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SearchIcon from "@mui/icons-material/Search";

import axios from "axios";
import { ICustomer } from "../../Api/Interface/api.interface";
import { useGetCustomerQuery, useGetNotificationsQuery } from "../../Api/attoDeskApi";

// ── Constants ──────────────────────────────────────────────────────────────────
const drawerWidth = 260;
const collapsedWidth = 68;
const headerHeight = 100;    // matches appStyles topbar height
const sidebarHeaderHeight = 64;

// ── Brand colors ───────────────────────────────────────────────────────────────
const BRAND = "#2f3891";        // indigo-500
const BRAND_DARK = "#2f3891";   // indigo-600
const ACTIVE_BG = "#eef2ff";    // indigo-50
const HOVER_BG = "#f8fafc";     // slate-50
const SIDEBAR_BG = "#ffffff";
const SECTION_COLOR = "#94a3b8"; // slate-400
const TEXT_COLOR = "#374151";    // gray-700
const TEXT_MUTED = "#9ca3af";    // gray-400
const BORDER_COLOR = "#e2e8f0";

// ── AppBar ─────────────────────────────────────────────────────────────────────
interface AppBarProps extends MuiAppBarProps { open?: boolean; }

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  height: headerHeight,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    paddingLeft: 0,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    marginLeft: collapsedWidth,
    width: `calc(100% - ${collapsedWidth}px)`,
    paddingLeft: 0,
    [theme.breakpoints.down("md")]: { marginLeft: 0, width: "100%", paddingLeft: 0 },
  }),
}));

// ── Drawer mixins ──────────────────────────────────────────────────────────────
const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  borderRight: `1px solid ${BORDER_COLOR}`,
  boxShadow: "2px 0 12px rgba(0,0,0,0.04)",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: collapsedWidth,
  borderRight: `1px solid ${BORDER_COLOR}`,
  boxShadow: "2px 0 12px rgba(0,0,0,0.04)",
});

// ── Styled components ──────────────────────────────────────────────────────────
const IsNotMobileDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: SIDEBAR_BG,
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      background: SIDEBAR_BG,
    },
  }),
}));

const MobileDrawer = styled(MuiDrawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    width: drawerWidth,
    height: "100vh",
    background: SIDEBAR_BG,
    borderRight: `1px solid ${BORDER_COLOR}`,
  },
}));

const DrawerHeader = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  height: sidebarHeaderHeight,
  padding: "0 12px",
  borderBottom: `1px solid ${BORDER_COLOR}`,
  flexShrink: 0,
  background: SIDEBAR_BG,
}));

const DrawerContent = styled("div")(() => ({
  background: SIDEBAR_BG,
  overflowY: "auto",
  overflowX: "hidden",
  flexGrow: 1,
  scrollbarWidth: "none",
  "-ms-overflow-style": "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

const DrawerFooter = styled("div")(() => ({
  borderTop: `1px solid ${BORDER_COLOR}`,
  padding: "12px 16px",
  display: "flex",
  alignItems: "center",
  gap: 10,
  background: SIDEBAR_BG,
  flexShrink: 0,
}));

const PaddingBody = styled("div")({ height: headerHeight });

// ── Types ──────────────────────────────────────────────────────────────────────
type SubmenuItem = {
  topBarTitle?: string;
  title: string;
  path: string;
  newPage?: boolean;
  icon: React.ReactElement;
  permissionKey?: string;
};

type menuList = {
  topBarTitle: string;
  title: string;
  icon: React.ReactElement;
  path: string;
  newPage?: boolean;
  submenuOpen?: boolean;
  submenus?: SubmenuItem[];
  permissionKey?: string;
};

// ── Menu data ──────────────────────────────────────────────────────────────────
const MainMenuList: { comomTitle: string; menuList?: menuList[] }[] = [
  {
    comomTitle: "General",
    menuList: [
      // Dashboard is the universal landing page; visible to every role. Each
      // KPI card on the page is itself permission-gated, so a driver sees a
      // role-appropriate view rather than an empty page or a redirect.
      { title: "Dashboard",       topBarTitle: "Welcome!",         icon: <DashboardOutlinedIcon />,      path: "/dashboard/list",      submenuOpen: false },
      { title: "Clients",         topBarTitle: "",                 icon: <PeopleOutlinedIcon />,          path: "/customer/list",       submenuOpen: false, permissionKey: "customers" },
      { title: "Trips",           topBarTitle: "Booking List",     icon: <DirectionsBusOutlinedIcon />,   path: "/driverBooking/list",  submenuOpen: false, permissionKey: "trips" },
      { title: "Quotes",          topBarTitle: "Quotes List",      icon: <FormatQuoteIcon />,             path: "/quote/list",          submenuOpen: false, permissionKey: "quotes" },
      {
        title: "Invoices", topBarTitle: "Invoices", icon: <ReceiptLongOutlinedIcon />,
        path: "#", submenuOpen: true, permissionKey: "invoices",
        submenus: [
          { title: "All Invoices",      topBarTitle: "Invoices List",              icon: <ReceiptLongOutlinedIcon />, path: "/invoice/list",          permissionKey: "invoices" },
          { title: "Contract Inv.",     topBarTitle: "Contract Invoices",          icon: <ArticleOutlinedIcon />,     path: "/contract-invoice/list", permissionKey: "contract_invoices" },
        ],
      },
      { title: "AP Email",        topBarTitle: "AP Email Sender",  icon: <AttachEmailOutlinedIcon />,     path: "/ap/email",            submenuOpen: false, permissionKey: "ap_email" },
      {
        title: "Driver", topBarTitle: "Driver", icon: <BadgeOutlinedIcon />,
        path: "#", submenuOpen: true, permissionKey: "drivers",
        submenus: [
          { title: "Schedule",  topBarTitle: "Driver Schedule",           icon: <CalendarMonthOutlinedIcon />,  path: "/tour/list",               permissionKey: "drivers" },
          { title: "Payments",  topBarTitle: "Driver Payments",           icon: <PaymentsOutlinedIcon />,       path: "/driver-payment/list",     permissionKey: "driver_payments" },
          { title: "Pay Roll",  topBarTitle: "Driver Pay Roll",           icon: <AccountBalanceOutlinedIcon />, path: "/driver-payroll",          permissionKey: "driver_payroll" },
          { title: "Reviews",   topBarTitle: "Driver Reviews & Feedback", icon: <RateReviewOutlinedIcon />,     path: "/driver-payment/reviews",  permissionKey: "driver_payments" },
        ],
      },
      { title: "Enquiries", topBarTitle: "Enquiries & Leads", icon: <InboxOutlinedIcon />, path: "/enquiry/list", submenuOpen: false, permissionKey: "leads" },
      {
        title: "Reports", topBarTitle: "Reports", icon: <AssessmentOutlinedIcon />,
        path: "/report/list", submenuOpen: true, permissionKey: "reports",
        submenus: [
          { title: "Reports",       topBarTitle: "Reports",       icon: <AssessmentOutlinedIcon />,        path: "/report/list",            permissionKey: "reports" },
          { title: "Balance Sheet", topBarTitle: "Balance Sheet", icon: <AccountBalanceOutlinedIcon />,    path: "/reports/balance-sheet",  permissionKey: "reports" },
        ],
      },
      { title: "Terminated",      topBarTitle: "Terminated Quotes",icon: <BlockIcon />,                   path: "/quote/terminate",     submenuOpen: false, permissionKey: "quotes" },

    ],
  },
  {
    comomTitle: "Expenses",
    menuList: [
      {
        title: "Expenses",
        topBarTitle: "Expenses",
        icon: <AccountBalanceWalletOutlinedIcon />,
        path: "#",
        submenuOpen: true,
        permissionKey: "expenses",
        submenus: [
          { title: "Dashboard",       topBarTitle: "Expense Dashboard",   icon: <DashboardCustomizeOutlinedIcon />, path: "/expense/dashboard",      permissionKey: "expenses" },
          { title: "Add Expense",     topBarTitle: "Add Expense",         icon: <AddCardOutlinedIcon />,            path: "/expense/create",         permissionKey: "expenses" },
          { title: "Manage Expenses", topBarTitle: "Manage Expenses",     icon: <TableChartOutlinedIcon />,         path: "/expense/list",           permissionKey: "expenses" },
          { title: "Categories",      topBarTitle: "Expense Categories",  icon: <CategoryOutlinedIcon />,           path: "/expense-category/list",  permissionKey: "expenses" },
          { title: "Reports",         topBarTitle: "Expense Reports",     icon: <BarChartOutlinedIcon />,           path: "/expense/reports",        permissionKey: "expenses" },
        ],
      },
    ],
  },
  {
    comomTitle: "Payments",
    menuList: [
      // A primary menu item, not a Settings sub-item: this is day-to-day work
      // (finding a customer's transaction) rather than configuration.
      { title: "Online Payments", topBarTitle: "Online Payments", icon: <CreditCardOutlinedIcon />, path: "/online-payments", submenuOpen: false, permissionKey: "online_payments" },
    ],
  },
  {
    comomTitle: "Users",
    menuList: [
      { title: "Drivers", topBarTitle: "Drivers List", icon: <BadgeOutlinedIcon />,     path: "/team/list",    submenuOpen: false, permissionKey: "drivers" },
      {
        title: "User Management", topBarTitle: "User Management", icon: <GroupsOutlinedIcon />,
        path: "#", submenuOpen: true, permissionKey: "users",
        submenus: [
          { title: "Users",      topBarTitle: "User Management",  icon: <GroupsOutlinedIcon />,       path: "/user-management/users",      permissionKey: "users" },
          { title: "User Roles", topBarTitle: "User Roles",       icon: <VerifiedUserOutlinedIcon />,  path: "/user-management/roles",     permissionKey: "users" },
          { title: "Privileges", topBarTitle: "Privileges",       icon: <SecurityOutlinedIcon />,      path: "/user-management/privileges", permissionKey: "users" },
        ],
      },
    ],
  },
  {
    comomTitle: "Setting",
    menuList: [
      {
        title: "Settings",
        topBarTitle: "Settings",
        icon: <TuneIcon />,
        path: "#",
        submenuOpen: true,
        permissionKey: "settings",
        submenus: [
          { title: "Business Profile", topBarTitle: "Business Profile",  icon: <StorefrontOutlinedIcon />,  path: "/business-profile", permissionKey: "settings" },
          { title: "Company",        topBarTitle: "Company List",        icon: <ApartmentOutlinedIcon />,   path: "/company/list",  permissionKey: "companies" },
          { title: "Bus & Services", topBarTitle: "Bus & Services List", icon: <DirectionsBusIcon />,       path: "/product/list",  permissionKey: "products" },
          { title: "Category",       topBarTitle: "Category List",       icon: <CategoryOutlinedIcon />,    path: "/category/list", permissionKey: "categories" },
          { title: "Looping",        topBarTitle: "Looping List",        icon: <LoopIcon />,                path: "/looping/list",  permissionKey: "settings" },
          { title: "Email",          topBarTitle: "Email Template List", icon: <EmailOutlinedIcon />,       path: "/email/list",    permissionKey: "email" },
          { title: "SMS",            topBarTitle: "SMS Template List",   icon: <SmsOutlinedIcon />,         path: "/sms/list",      permissionKey: "sms" },
          { title: "Note",           topBarTitle: "Note Template List",  icon: <NoteAltOutlinedIcon />,     path: "/note/list",     permissionKey: "notes" },
          { title: "URL",            topBarTitle: "URL List",            icon: <LinkIcon />,                path: "/url/list",      permissionKey: "settings" },
        ],
      },
    ],
  },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function MiniDrawer({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(true);
  const { hasPermission, roleName } = usePermissions();
  const branding = useBranding();

  // Filter menu items based on user's role permissions
  const canSeeMenuItem = useCallback((item: { permissionKey?: string }) => {
    if (!item.permissionKey) return true;
    return hasPermission(item.permissionKey, "can_view");
  }, [hasPermission]);

  const filteredMenuList = useMemo(() => {
    return MainMenuList.map((section) => {
      if (!section.menuList) return section;
      const filtered = section.menuList.filter((ml) => {
        // For items with submenus, show parent if ANY submenu is visible
        if (ml.submenus && ml.submenus.length > 0) {
          return ml.submenus.some(canSeeMenuItem);
        }
        // For standalone items, check the item's own permission
        return canSeeMenuItem(ml);
      }).map((ml) => {
        if (!ml.submenus) return ml;
        return { ...ml, submenus: ml.submenus.filter(canSeeMenuItem) };
      });
      return { ...section, menuList: filtered };
    }).filter((section) => section.menuList && section.menuList.length > 0);
  }, [canSeeMenuItem]);

  // ── Sidebar search ──────────────────────────────────────────────────────────
  const [menuSearch, setMenuSearch] = useState("");

  const searchFilteredMenuList = useMemo(() => {
    const q = menuSearch.trim().toLowerCase();
    if (!q) return filteredMenuList;
    return filteredMenuList.map((section) => {
      if (!section.menuList) return { ...section, menuList: [] };
      const filtered = section.menuList.map((ml) => {
        const parentMatch = ml.title.toLowerCase().includes(q);
        if (!ml.submenus || ml.submenus.length === 0) {
          return parentMatch ? ml : null;
        }
        const matchingSubs = ml.submenus.filter((s) => s.title.toLowerCase().includes(q));
        if (parentMatch) return ml;
        if (matchingSubs.length > 0) return { ...ml, submenus: matchingSubs };
        return null;
      }).filter(Boolean) as menuList[];
      return { ...section, menuList: filtered };
    }).filter((section) => section.menuList && section.menuList.length > 0);
  }, [filteredMenuList, menuSearch]);

  const [notificationPopUp, setNotificationPopUp] = React.useState<HTMLElement | null>(null);
  const { mode, toggleTheme } = useThemeContext();
  const [profilePopUp, setProfilePopUp] = useState<HTMLElement | null>(null);
  const openProfilePopUp = Boolean(profilePopUp);
  const openNotificationPopUp = Boolean(notificationPopUp);

  const [currentMenuName, setCurrentMenuName] = React.useState("");
  const [hoveredMenuName, setHoveredMenuName] = React.useState("");
  const [expandMenu, setExpandMenu] = React.useState<string>("");
  const [topBarTitle, setTopBarTitle] = useState("");
  const [hoveredSubmenu, setHoveredSubmenu] = React.useState("");
  const [selectedSubmenu, setSelectedSubmenu] = React.useState("");
  const id = location.pathname.split("/").pop();
  const [currentCustomer, setCurrentCustomer] = useState<ICustomer | null>(null);

  interface UserInfo { id: number; username: string; token: string; user_id: number; }
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const API_URL = process.env.REACT_APP_API_URL;

  React.useEffect(() => { window.scrollTo(0, 0); }, [currentMenuName]);

  React.useEffect(() => {
    let found = false;
    // Handle enquiry detail page — highlight Enquiries menu item
    if (location.pathname.match(/^\/enquiry\/\d+$/)) {
      setCurrentMenuName("/enquiry/list"); setExpandMenu("Enquiries"); found = true;
    }
    if (!found) {
      for (const mainMenu of MainMenuList) {
        for (const menu of mainMenu.menuList || []) {
          if (menu.path === location.pathname) {
            setCurrentMenuName(menu.path); setSelectedSubmenu(""); setExpandMenu(menu.title); found = true;
          }
          for (const submenu of menu.submenus || []) {
            if (submenu.path === location.pathname) {
              setSelectedSubmenu(submenu.path); setExpandMenu(menu.title); setHoveredSubmenu(submenu.path); found = true;
            }
          }
          if (found) break;
        }
        if (found) break;
      }
    }
  }, [location.pathname]);

  React.useEffect(() => {
    const findTopBarTitle = (): string => {
      if (location.pathname.startsWith("/customer-history/")) return "Client Details";
      if (location.pathname.startsWith("/product-history/")) return "Product Details";
      if (location.pathname.startsWith("/company-history/")) return "Company Details & Edit";
      if (location.pathname.startsWith("/quote-history/")) return "Quote Edit";
      if (location.pathname.startsWith("/work-order/history/")) return "Quote Details";
      if (location.pathname.startsWith("/invoice/history/")) return "Invoice Details";
      if (location.pathname.startsWith("/team-history/")) return "User Details";
      if (location.pathname.startsWith("/customer-history/project/")) return "Customer's Project Details";
      if (location.pathname.match(/^\/enquiry\/\d+$/)) return "Enquiry Details";
      if (location.pathname.startsWith("/reports/balance-sheet")) return "Balance Sheet";
      if (location.pathname.startsWith("/expense/dashboard")) return "Expense Dashboard";
      if (location.pathname.startsWith("/expense/reports")) return "Expense Reports";
      if (location.pathname.startsWith("/expense/create")) return "Add Expense";
      if (location.pathname.startsWith("/expense/")) return "Edit Expense";
      if (location.pathname.startsWith("/user-management/users"))      return "User Management";
      if (location.pathname.startsWith("/user-management/roles"))      return "User Roles";
      if (location.pathname.startsWith("/user-management/privileges")) return "Privileges";
      if (location.pathname.startsWith("/looping/create")) return "Create Looping";
      if (location.pathname.startsWith("/department/create")) return "Create Department";
      if (location.pathname.startsWith("/coursing/create")) return "Create Coursing";
      if (location.pathname.startsWith("/brand/create")) return "Create Product Brand";
      if (location.pathname.startsWith("/tag/create")) return "Create Product Tag";
      if (location.pathname.startsWith("/tax/create")) return "Create Tax";
      if (location.pathname.startsWith("/printer/create")) return "Create Printer";
      if (location.pathname.startsWith("/email/create")) return "Create Email Template";
      if (location.pathname.startsWith("/note/create")) return "Create Note Template";
      for (const mainMenu of MainMenuList) {
        for (const menu of mainMenu.menuList || []) {
          if (menu.path === location.pathname) return menu.topBarTitle;
          for (const submenu of menu.submenus || []) {
            if (submenu.path === location.pathname) return submenu.topBarTitle || "WELCOME!";
          }
        }
      }
      return "";
    };
    setTopBarTitle(findTopBarTitle());
  }, [location.pathname]);

  // Allow any page to push a dynamic top-bar title (e.g. "Invoice Details" vs "Quote Details")
  React.useEffect(() => {
    const handler = (e: Event) => {
      const title = (e as CustomEvent<string>).detail;
      if (title) setTopBarTitle(title);
    };
    window.addEventListener("topBarTitleChange", handler);
    return () => window.removeEventListener("topBarTitleChange", handler);
  }, []);

  const { data: customerData } = useGetCustomerQuery({ searchText: "", id: "" });
  const { data: notificationData } = useGetNotificationsQuery(undefined, { pollingInterval: 30000 });
  const unreadNotificationCount: number = (notificationData as any)?.unread_count ?? 0;
  const customer: ICustomer[] = useMemo(() => {
    if (customerData && Array.isArray(customerData.data)) return customerData.data as ICustomer[];
    return [];
  }, [customerData]);

  useEffect(() => {
    if (id) {
      const found = customer.find((p) => String(p.id) === String(id)) || null;
      if (found) { setCurrentCustomer(found); localStorage.setItem("lastCustomer", JSON.stringify(found)); }
    } else {
      const saved = localStorage.getItem("lastCustomer");
      if (saved) setCurrentCustomer(JSON.parse(saved) as ICustomer);
    }
  }, [id, customer]);

  React.useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await axios.post(`${API_URL}/user/userInfo`, {}, {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        setUserInfo(response.data.data);
      } catch (error) { console.error("Error fetching user info", error); }
    };
    fetchUserInfo();
  }, []);

  const handleDrawerToggle = () => { setOpen(!open); setExpandMenu(""); };
  const handleMobileDrawerToggle = () => { setMobileDrawerOpen(!mobileDrawerOpen); };
  const handleMobileDrawerClose = () => { setMobileDrawerOpen(false); };
  const handleClickNotificationPopUp = (e: React.MouseEvent<HTMLElement>) => setNotificationPopUp(e.currentTarget);
  const handleClickProfilePopUp = (e: React.MouseEvent<HTMLElement>) => setProfilePopUp(e.currentTarget);
  const handleCloseProfilePopUp = () => setProfilePopUp(null);
  const handleCloseNotificationPopUp = () => setNotificationPopUp(null);

  const handleMainMenuItemClick = (ml: menuList) => {
    if (ml.submenus && ml.submenus.length > 0) {
      setExpandMenu(ml.title === expandMenu ? "" : ml.title);
    } else if (ml.path) {
      setCurrentMenuName(ml.path); setSelectedSubmenu(""); navigate(ml.path); setMobileDrawerOpen(false);
    }
    setTopBarTitle(ml.topBarTitle || "WELCOME!");
  };

  const handleSubmenuItemClicked = (submenu: SubmenuItem) => {
    setCurrentMenuName(submenu.path); setSelectedSubmenu(submenu.path);
    navigate(submenu.path); setTopBarTitle(submenu.topBarTitle || "WELCOME!"); setMobileDrawerOpen(false);
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const isMenuActive = (ml: menuList) =>
    selectedSubmenu?.startsWith(ml.path) || currentMenuName === ml.path;

  const isSubmenuActive = (submenu: SubmenuItem) =>
    selectedSubmenu === submenu.path || currentMenuName === submenu.path;

  // ── Shared drawer content renderer ──────────────────────────────────────────
  const renderMenuItems = (sidebarOpen: boolean) => (
    <DrawerContent>
      {/* Sidebar search */}
      {sidebarOpen && (
        <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
          <Box sx={{
            display: "flex", alignItems: "center", gap: 0.75,
            px: 1.25, height: 34, borderRadius: 2,
            bgcolor: HOVER_BG, border: `1px solid ${BORDER_COLOR}`,
            transition: "all 0.15s ease",
            "&:focus-within": { borderColor: BRAND, boxShadow: `0 0 0 2px ${BRAND}20` },
          }}>
            <SearchIcon sx={{ fontSize: 15, color: SECTION_COLOR, flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search menu..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              style={{
                border: "none", outline: "none", background: "transparent",
                fontSize: 12, color: TEXT_COLOR, width: "100%", fontFamily: "inherit",
              }}
            />
            {menuSearch && (
              <Box component="span" onClick={() => setMenuSearch("")}
                sx={{ fontSize: 14, cursor: "pointer", color: SECTION_COLOR, lineHeight: 1, flexShrink: 0, "&:hover": { color: TEXT_COLOR } }}>
                x
              </Box>
            )}
          </Box>
        </Box>
      )}
      {searchFilteredMenuList.map((section, si) => (
        <React.Fragment key={si}>
          {/* Section label */}
          {sidebarOpen ? (
            <Box sx={{ px: 2.5, pt: si === 0 ? 2.5 : 2, pb: 0.5 }}>
              <Typography sx={{
                fontSize: 10, fontWeight: 700, color: SECTION_COLOR,
                textTransform: "uppercase", letterSpacing: "0.1em",
              }}>
                {section.comomTitle}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ px: 1, pt: si === 0 ? 2 : 1.5, pb: 0.5 }}>
              <Box sx={{ height: 1, bgcolor: BORDER_COLOR, mx: 0.5 }} />
            </Box>
          )}

          {section.menuList && (
            <List disablePadding sx={{ px: sidebarOpen ? 1.5 : 0.75, pb: 0.5 }}>
              {section.menuList.map((ml) => {
                const active = isMenuActive(ml);
                const isExpanded = expandMenu === ml.title || (!!menuSearch.trim() && !!(ml.submenus && ml.submenus.length > 0));
                const hasSubmenu = !!(ml.submenus && ml.submenus.length > 0);

                const itemContent = (
                  <ListItemButton
                    component={ml.path !== "#" ? Link : "div"}
                    to={ml.path !== "#" ? ml.path : undefined}
                    onClick={() => handleMainMenuItemClick(ml)}
                    sx={{
                      borderRadius: 2,
                      px: sidebarOpen ? 1.5 : 1,
                      py: 0.9,
                      mb: 0.25,
                      minHeight: 40,
                      justifyContent: sidebarOpen ? "flex-start" : "center",
                      bgcolor: active ? ACTIVE_BG : "transparent",
                      borderLeft: active ? `3px solid ${BRAND}` : "3px solid transparent",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        bgcolor: active ? ACTIVE_BG : HOVER_BG,
                        "& .nav-icon": { color: active ? BRAND_DARK : BRAND },
                        "& .nav-text": { color: active ? BRAND_DARK : TEXT_COLOR },
                      },
                    }}
                  >
                    <ListItemIcon
                      className="nav-icon"
                      sx={{
                        minWidth: sidebarOpen ? 36 : 0,
                        color: active ? BRAND : TEXT_MUTED,
                        transition: "color 0.15s ease",
                        "& svg": { fontSize: 19 },
                      }}
                    >
                      {ml.icon as React.ReactNode}
                    </ListItemIcon>

                    {sidebarOpen && (
                      <>
                        <ListItemText
                          primary={ml.title}
                          className="nav-text"
                          primaryTypographyProps={{
                            fontSize: 13,
                            fontWeight: active ? 700 : 500,
                            color: active ? BRAND_DARK : TEXT_COLOR,
                            noWrap: true,
                          }}
                        />
                        {hasSubmenu && (
                          isExpanded
                            ? <ExpandLessIcon sx={{ fontSize: 17, color: TEXT_MUTED }} />
                            : <ExpandMoreIcon sx={{ fontSize: 17, color: TEXT_MUTED }} />
                        )}
                      </>
                    )}
                  </ListItemButton>
                );

                return (
                  <ListItem key={ml.title} disablePadding sx={{ display: "block" }}>
                    {sidebarOpen ? itemContent : (
                      <Tooltip title={ml.title} placement="right" arrow>
                        {itemContent}
                      </Tooltip>
                    )}

                    {/* Submenu */}
                    {hasSubmenu && sidebarOpen && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List disablePadding sx={{ pl: 1 }}>
                          {(ml.submenus || []).map((sub) => {
                            const subActive = isSubmenuActive(sub);
                            return (
                              <ListItem key={sub.title} disablePadding>
                                <ListItemButton
                                  component={Link}
                                  to={sub.path}
                                  onClick={() => handleSubmenuItemClicked(sub)}
                                  sx={{
                                    borderRadius: 2,
                                    pl: 2,
                                    pr: 1.5,
                                    py: 0.7,
                                    mb: 0.25,
                                    minHeight: 36,
                                    bgcolor: subActive ? ACTIVE_BG : "transparent",
                                    borderLeft: subActive ? `3px solid ${BRAND}` : "3px solid transparent",
                                    transition: "all 0.15s ease",
                                    "&:hover": { bgcolor: subActive ? ACTIVE_BG : HOVER_BG },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 28, "& svg": { fontSize: 15 }, color: subActive ? BRAND : TEXT_MUTED }}>
                                    {sub.icon as React.ReactNode}
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={sub.title}
                                    primaryTypographyProps={{
                                      fontSize: 12,
                                      fontWeight: subActive ? 700 : 500,
                                      color: subActive ? BRAND_DARK : TEXT_COLOR,
                                      noWrap: true,
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </ListItem>
                );
              })}
            </List>
          )}
        </React.Fragment>
      ))}

      {/* Bottom spacer */}
      <Box sx={{ flexGrow: 1, minHeight: 16 }} />
    </DrawerContent>
  );

  // ── User footer ──────────────────────────────────────────────────────────────
  const renderFooter = (sidebarOpen: boolean) => (
    <DrawerFooter>
      <Avatar
        alt={userInfo?.username || "User"}
        src={UserImg}
        sx={{ width: 32, height: 32, flexShrink: 0, border: `2px solid ${BORDER_COLOR}` }}
      />
      {sidebarOpen && (
        <Box sx={{ overflow: "hidden" }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_COLOR, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {userInfo?.username || "User"}
          </Typography>
          <Typography sx={{ fontSize: 10, color: TEXT_MUTED }}>{roleName || "User"}</Typography>
        </Box>
      )}
    </DrawerFooter>
  );

  // ── Logo header ──────────────────────────────────────────────────────────────
  const renderDrawerHeader = (sidebarOpen: boolean, onToggle: () => void) => (
    <DrawerHeader>
      {sidebarOpen ? (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", overflow: "hidden", pr: 1 }}>
          {/* Settings → Business Profile → Main Logo, else the built-in logo. */}
          <img
            src={branding.main_logo_url || LogoMain}
            alt={branding.business_name || "TJBus"}
            style={{ height: 36, width: "auto", maxWidth: "100%", display: "block", cursor: "pointer", objectFit: "contain" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = LogoMain; }}
            onClick={() => navigate("/dashboard/list")}
          />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <img
            src={branding.favicon_url || branding.main_logo_url || LogoFav}
            alt={branding.business_name || "TJBus"}
            style={{ height: 28, width: 28, objectFit: "contain", cursor: "pointer" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = LogoFav; }}
            onClick={() => navigate("/dashboard/list")}
          />
        </Box>
      )}
      <IconButton
        onClick={onToggle}
        size="small"
        sx={{
          width: 28, height: 28, borderRadius: 1.5,
          bgcolor: HOVER_BG, border: `1px solid ${BORDER_COLOR}`,
          color: TEXT_MUTED, flexShrink: 0,
          "&:hover": { bgcolor: ACTIVE_BG, color: BRAND },
        }}
      >
        {sidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </DrawerHeader>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex" }}>
      {!isMobile ? (
        <Box sx={{ display: "flex" }}>
          <CssBaseline />

          {/* ── Top App Bar ── */}
          <AppBar position="fixed" open={open} className={classes.topbar}>
            <Toolbar className={classes.toolbar}>
              <Typography width={"100%"} className={classes.h4}>
                {topBarTitle}
              </Typography>
              <Grid container spacing={1} alignItems="center" justifyContent="flex-end" sx={{ display: "flex" }}>
                <Grid item>
                  {currentCustomer && (
                    <Chip
                      label={(currentCustomer.customerCompanyName || `${currentCustomer.customerFirstName || ""} ${currentCustomer.customerLastName || ""}`.trim()).replace(/\b\w/g, c => c.toUpperCase())}
                      onClick={() => navigate(`/customer-history/${currentCustomer.id}`)}
                      sx={{ mr: 1, cursor: "pointer", fontWeight: 700, bgcolor: '#2f3891', color: 'white', fontSize: '0.85rem', '&:hover': { bgcolor: '#252e7a' } }}
                    />
                  )}
                  <IconButton onClick={handleClickNotificationPopUp} className={classes.topbarButton} aria-haspopup="true">
                    <Badge badgeContent={unreadNotificationCount} color="error" max={99}>
                      <MdOutlineNotificationsNone className={classes.topbarIcon} />
                    </Badge>
                  </IconButton>
                  <IconButton onClick={toggleTheme} aria-haspopup="true" className={classes.topbarButton}>
                    <MdDarkMode className={classes.topbarIcon} />
                  </IconButton>
                  <IconButton onClick={handleClickProfilePopUp} className={classes.topbarButton} aria-haspopup="true">
                    <Avatar alt="User" src={UserImg} className={classes.topbarIconProfile} />
                  </IconButton>
                </Grid>
                <Grid item>
                  <div className={classes.appSearch}>
                    <Typography width={"100%"} className={classes.h5}>{userInfo?.username}</Typography>
                  </div>
                </Grid>
              </Grid>
              <Profile profile={profilePopUp} onClose={handleCloseProfilePopUp} open={openProfilePopUp} />
              <Notification notification={notificationPopUp} onClose={handleCloseNotificationPopUp} open={openNotificationPopUp} />
            </Toolbar>
          </AppBar>

          {/* ── Sidebar Drawer ── */}
          <IsNotMobileDrawer variant="permanent" open={open}>
            {renderDrawerHeader(open, handleDrawerToggle)}
            {renderMenuItems(open)}
            {renderFooter(open)}
          </IsNotMobileDrawer>
        </Box>
      ) : (
        /* ── Mobile ── */
        <Box sx={{ display: "flex" }}>
          <AppBar position="fixed" className={classes.mobileTopbar}>
            <Toolbar className={classes.toolbar}>
              <Grid container justifyContent="space-between" sx={{ width: "100%" }} alignItems="center">
                <Grid item md={1} sm={1}>
                  <IconButton
                    onClick={handleMobileDrawerToggle}
                    edge="start"
                    sx={{ ...(mobileDrawerOpen && { display: "none" }) }}
                    className={classes.topbarButton}
                  >
                    {mobileDrawerOpen ? <CloseIcon className={classes.topbarIcon} /> : <MenuIcon className={classes.topbarIcon} />}
                  </IconButton>
                </Grid>
                <Grid item md={2} sm={2}>
                  <Typography width={"100%"} className={classes.h4}>{topBarTitle}</Typography>
                </Grid>
                <Grid item md={9} sm={9} justifyContent="end" alignItems="end">
                  <Grid container spacing={1} alignItems="end" justifyContent="flex-end" sx={{ display: "flex" }}>
                    <Grid item>
                      {currentCustomer && (
                        <Chip
                          label={(currentCustomer.customerCompanyName || `${currentCustomer.customerFirstName || ""} ${currentCustomer.customerLastName || ""}`.trim()).replace(/\b\w/g, c => c.toUpperCase())}
                          onClick={() => navigate(`/customer-history/${currentCustomer.id}`)}
                          sx={{ mr: 1, cursor: "pointer", fontWeight: 700, bgcolor: '#2f3891', color: 'white', fontSize: '0.85rem', '&:hover': { bgcolor: '#252e7a' } }}
                        />
                      )}
                      <IconButton onClick={handleClickNotificationPopUp} className={classes.topbarButton} aria-haspopup="true">
                        <Badge badgeContent={unreadNotificationCount} color="error" max={99}>
                          <MdOutlineNotificationsNone className={classes.topbarIcon} />
                        </Badge>
                      </IconButton>
                      <IconButton onClick={toggleTheme} aria-haspopup="true" className={classes.topbarButton}>
                        <MdDarkMode className={classes.topbarIcon} />
                      </IconButton>
                      <IconButton onClick={handleClickProfilePopUp} className={classes.topbarButton} aria-haspopup="true">
                        <Avatar alt="User" src={UserImg} className={classes.topbarIconProfile} />
                      </IconButton>
                    </Grid>
                    <Grid item sx={{ [theme.breakpoints.down("sm")]: { display: "none" } }}>
                      <div className={classes.appSearch}>
                        <TextField
                          InputProps={{
                            startAdornment: <InputAdornment position="start"><CiSearch className={classes.searchWidgetIcon} /></InputAdornment>,
                            classes: { root: classes.formControl, notchedOutline: classes.notchedOutline },
                          }}
                          fullWidth placeholder="Search .." variant="outlined" size="small"
                        />
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Profile profile={profilePopUp} onClose={handleCloseProfilePopUp} open={openProfilePopUp} />
              <Notification notification={notificationPopUp} onClose={handleCloseNotificationPopUp} open={openNotificationPopUp} />
            </Toolbar>

            <MobileDrawer open={mobileDrawerOpen} onClose={handleMobileDrawerClose}>
              {renderDrawerHeader(true, handleMobileDrawerClose)}
              {renderMenuItems(true)}
              {renderFooter(true)}
            </MobileDrawer>
          </AppBar>
        </Box>
      )}

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: open && !isMobile ? `calc(100% - ${drawerWidth}px)` : isMobile ? "100%" : `calc(100% - ${collapsedWidth}px)`,
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <PaddingBody />
        {children}
      </Box>
    </Box>
  );
}
